import { ReactElement, useMemo } from 'react'

import { Autocomplete } from 'components/v2/Autocomplete'
import { IAutocompleteProps } from 'components/v2/Autocomplete/types'
import { convertHour24ToHour12Time, createTimeInterval } from 'utils/time'

const DEFAULT_INTERVAL = 60 // minutes

export interface ISelectedTime {
  id: string
  value: string
}

interface ISelectTimeProps extends Omit<IAutocompleteProps, 'items' | 'getItemLabel'> {
  value: string
  onChange: (time: string) => void
  interval?: number
  isEndOfInterval?: boolean
}

/**
 * SelectTime component props
 * @typedef ISelectTimeProps
 * @property {string} value - selected time
 * @property {function} onChange - callback to handle time change
 * @property {number} [interval] - time interval
 */
export const SelectTime = ({
  value,
  onChange,
  interval = DEFAULT_INTERVAL,
  isEndOfInterval,
  ...props
}: ISelectTimeProps): ReactElement => {
  const times = useMemo(() => {
    return createTimeInterval({ interval, isEndOfInterval }).map((time) => ({
      id: time,
      value: time
    }))
  }, [interval, isEndOfInterval])

  const handleChange = (time: ISelectedTime): void => {
    onChange(time.value)
  }

  return (
    <Autocomplete
      {...props}
      items={times}
      getItemLabel={(item: ISelectedTime): string => {
        return convertHour24ToHour12Time(item.value)
      }}
      value={convertHour24ToHour12Time(value)}
      onChange={handleChange}
      disableKeyboard
    />
  )
}
